import { ref, computed, h, watch, defineAsyncComponent } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter, useRoute } from 'vue-router'
import { useMessage } from 'naive-ui'
import type { DropdownOption, TreeDropInfo } from 'naive-ui'
import { useLocalStorage } from '@vueuse/core'
import { useNavigatorStore } from '@/store/navigator'
import { useContextMenu } from '@/components/contextMenu'
import { useAppDialog } from '@/components/appDialog'
import { Nav } from '@teams/common/models'
import useMitt from '@/hooks/useMitt'
import { events } from '@/constants'
import ConfirmRemoveNav from './ConfirmRemoveNav.vue'
import { useAcl } from 'vue-simple-acl'
import aclRules from '@teams/common/acl/aclRules'
import usePreference from '@teams/common/hooks/usePreference'
import UpdateNav from '@/components/navigator/AddOrUpdateNav/entry.vue'
import useAddOrUpdateNav from '@teams/common/hooks/useAddOrUpdateNav/index'
import { useGroupStore } from '@/store/group'
import { useUserStore } from '@/store/user'
import useViewConfig from '@/hooks/useViewConfig'
import { buildTree, findSiblingsAndIndex } from '@teams/common/utils'
import Icon from '@teams/common/components/icon'
import type { TreeNavOption } from './types'
import handleResponseError from '@teams/common/hooks/useResponseError'
import { INavInfo } from '@teams/common/hooks/useAddOrUpdateNav/types'

enum NavBarCommand {
  ChangeNavBarPosition = 'ChangeNavBarPosition'
}

enum NavCommand {
  Reload = 'Reload',
  OpenInNewTab = 'OpenInNewTab',
  AddSubNav = 'AddSubNav',
  Update = 'Update',
  OpenSetPermissionDialog = 'openSetPermissionDialog',
  ChangeNavBarPosition = 'ChangeNavBarPosition',
  Remove = 'Remove'
}
type TreeNode<T, U> = {
  key?: any
  children?: TreeNode<T, U>[] | []
} & U
function useNav() {
  const router = useRouter()
  const route = useRoute()
  const message = useMessage()
  const emitter = useMitt()
  const groupStore = useGroupStore()
  const userStore = useUserStore()
  const { addBlankNav, setNewNav } = useAddOrUpdateNav({})

  const { t } = useI18n()
  // eslint-disable-next-line
  const $tt = (key: string, ...params: any[]) => {
    // @ts-ignore
    return t(`components.navigator.navs.${key}`, ...params)
  }

  const navigatorStore = useNavigatorStore()
  const contextMenu = useContextMenu()
  const dialog = useAppDialog()
  const preference = usePreference({ defaultClient: 'pc' })
  const { toggleCustomPanel } = useViewConfig()
  const acl = useAcl()
  const rbacDeleteTag = computed(() => acl.can(aclRules.navigation_delete_tag))
  const rbacUpdateTag = computed(() => acl.can(aclRules.navigation_update_tag))
  const rbacAddTag = computed(() => acl.can(aclRules.navigation_add_tag)) // 拥有“创建标签”权限才显示添加子标签
  const rbacChangeLayout = computed(() =>
    acl.can(aclRules.navigation_custom_interface_entrance)
  )
  const navSortable = computed(() =>
    acl.can(aclRules.navigation_update_tag_order)
  )

  const navs = computed(() => navigatorStore.list)
  const tempNavs = ref<Nav[] | undefined>() // 用于排序未操作成功前的临时生成的导航列表
  const storageExpandedNavIds = useLocalStorage<number[]>(
    `${groupStore.groupId}:${userStore.userId}:asideTreeNavExpandKeys`,
    []
  )

  const expandedNavIds = computed(() => {
    const navIdMap = navs.value.reduce((prev, nav) => {
      prev[nav.id] = true
      return prev
    }, {} as Record<number, boolean>)
    return storageExpandedNavIds.value.filter(key => !!navIdMap[key])
  })
  function onExpandedNavIdsChange(keys: number[]) {
    storageExpandedNavIds.value = keys
  }

  const activeNavId = computed(() => Number(route.query.nid))

  const checkIsNavActive = (nav: Nav) => nav.id === activeNavId.value

  const selectedNav = ref<Nav>() // 右键菜单选中时的导航

  const addNav = async () => {
    //  创建空白标签（根节点）
    const navid = await addBlankNav()
    router.push({ name: 'Nav', query: { nid: navid } })
    // 新建标签页
  }
  // 固定到导航栏
  const setNav = async (params: INavInfo) => {
    try {
      await setNewNav(params)
      message.success('已固定至协作空间导航栏')
    } catch (error: any) {
      // 40300001：没有固定到导航栏权限的错误代码
      if (error.response?.data.code === 40300001) {
        message.error('你没有执行该操作的权限')
      } else {
        handleResponseError(error)
      }
    }
  }
  // TODO: 新建标签-新建标签页重构
  const addOrUpdateNavDone = () => {
    if (selectedNav.value) {
      emitter.emit(events.updateNav, selectedNav.value)
    }
    selectedNav.value = undefined
    dialog.close()
  }

  const reloadNav = (nav: Nav) => {
    emitter.emit(events.reloadNav, nav)
  }

  const doRemoveNav = async (nav: Nav) => {
    try {
      if (navs.value.length <= 1) {
        message.warning('至少需保留一个标签页')
        dialog.close()
        return
      }
      await navigatorStore.removeNav(nav.id)
      message.success(t('base.removeSuccessfully'))
      emitter.emit(events.removeNav, nav)
      dialog.close()
    } catch (e) {
      handleResponseError(e)
    }
  }

  const confirmRemoveNav = (nav: Nav) => {
    const hasChildren = navs.value.find(n => n.parentId === nav.id)
    const tips = $tt(
      hasChildren ? 'removeNavWithChildrenTips' : 'removeNavWithoutChildrenTips'
    )
    dialog.show({
      title: $tt('confirmRemoveNavTitle'),
      modalProps: { style: { width: '480px' } },
      component: ConfirmRemoveNav,
      componentProps: {
        text: tips
      },
      componentEvents: {
        confirm: () => doRemoveNav(nav)
      },
      footer: false
    })
  }

  const openNavInNewTab = (nav: Nav) => {
    window.open(nav.url)
  }

  const addSubNav = async (nav: Nav) => {
    // 创建空白子标签
    const navid = await addBlankNav({ parentId: nav.id })
    router.push({ name: 'Nav', query: { nid: navid } })
    // 新建子标签时，自动展开父标签
    if (!storageExpandedNavIds.value.includes(nav.id)) {
      storageExpandedNavIds.value = [...storageExpandedNavIds.value, nav.id]
    }
  }

  const updateNav = (nav: Nav) => {
    dialog.show({
      title: '修改标签页',
      component: UpdateNav,
      componentProps: {
        initialValue: nav
      },
      modalProps: {
        autoFocus: false
      },
      modalStyle: {
        width: '480px'
      },
      footer: false
    })
  }
  const SetPermissionDialog = defineAsyncComponent(
    () => import('./SetPermissionDialog.vue')
  )

  const openSetPermissionDialog = (nav: Nav) => {
    dialog.show({
      name: 'SetPermissionDialog',
      title: '该标签页对谁可见',
      component: SetPermissionDialog,
      showCloseIcon: true,
      componentProps: { navId: nav.id, appId: 0 },
      footer: false,
      modalStyle: {
        width: '320px'
      },
      size: 'minWidth320'
    })
  }

  const changeNavBarLayout = () => {
    const isShowCustom = toggleCustomPanel()
    if (isShowCustom) {
      message.info('可在右侧面板中更改导航栏布局')
    }
  }

  const navBarCommandHandlers: Record<NavBarCommand, () => void> = {
    [NavBarCommand.ChangeNavBarPosition]: changeNavBarLayout
  }

  const navCommandHandlers: Record<NavCommand, (nav: Nav) => void> = {
    [NavCommand.Reload]: reloadNav,
    [NavCommand.OpenInNewTab]: openNavInNewTab,
    [NavCommand.AddSubNav]: addSubNav,
    [NavCommand.Update]: updateNav,
    [NavCommand.OpenSetPermissionDialog]: openSetPermissionDialog,
    [NavCommand.ChangeNavBarPosition]: changeNavBarLayout,
    [NavCommand.Remove]: confirmRemoveNav
  }

  const handleNavBarComand = (key: NavBarCommand) => {
    const handler = navBarCommandHandlers[key]
    handler?.()
    contextMenu.close()
  }

  const handleNavCommand = (nav: Nav, key: NavCommand) => {
    selectedNav.value = nav
    const handler = navCommandHandlers[key]
    handler?.(nav)
    contextMenu.close()
  }

  const navBarOptions = computed(() => [
    {
      label: '更改导航栏布局',
      key: NavCommand.ChangeNavBarPosition
    }
  ])

  const navCommandCategories = {
    page: 'page',
    operations: 'operations',
    layout: 'layout',
    remove: 'remove'
  }

  const navOptions = computed(() => {
    const isBlankNav = selectedNav.value?.type === 'blank'
    const isNavActive = selectedNav.value && checkIsNavActive(selectedNav.value)
    const options = [
      {
        label: t('base.refresh'),
        key: NavCommand.Reload,
        icon: () => h(Icon, { name: 'refreshNav', fill: 'transparent' }),
        filter: isNavActive,
        category: navCommandCategories.page
      },
      {
        label: '新页面打开',
        key: NavCommand.OpenInNewTab,
        icon: () => h(Icon, { name: 'openInNewPage', fill: 'transparent' }),
        filter: !isBlankNav,
        category: navCommandCategories.page
      },
      {
        type: 'divider',
        key: 'd1',
        // 此分割线前后有显示时保留 d1
        categorySpliter: `${navCommandCategories.page}:${navCommandCategories.operations}`
      },
      {
        label: '添加子标签页',
        key: NavCommand.AddSubNav,
        icon: () => h(Icon, { name: 'addLabel', fill: 'transparent' }),
        filter: rbacAddTag.value,
        category: navCommandCategories.operations
      },
      {
        label: $tt('updateNav'),
        key: NavCommand.Update,
        icon: () => h(Icon, { name: 'edit', fill: 'transparent' }),
        filter: rbacUpdateTag.value && !isBlankNav,
        category: navCommandCategories.operations
      },
      {
        label: '设置可见性',
        key: NavCommand.OpenSetPermissionDialog,
        icon: () => h(Icon, { name: 'setPermission', fill: 'transparent' }),
        filter: rbacUpdateTag.value && !isBlankNav,
        category: navCommandCategories.operations
      },
      {
        type: 'divider',
        key: 'd2',
        // 此分割线前有显示时保留 d2
        categorySpliter: `${navCommandCategories.operations}:${navCommandCategories.layout}`
      },
      {
        label: '更改导航栏布局',
        key: NavCommand.ChangeNavBarPosition,
        icon: () => h(Icon, { name: 'changleNavLayout', fill: 'transparent' }),
        filter: rbacChangeLayout.value,
        category: navCommandCategories.layout
      },
      {
        type: 'divider',
        key: 'd3',
        // 显示 Remove 时保留 d2
        categorySpliter: `${navCommandCategories.layout}:${navCommandCategories.remove}`
      },
      {
        label: '移除',
        key: NavCommand.Remove,
        icon: () => h(Icon, { name: 'redDelete', fill: 'transparent' }),
        props: { class: 'special-color' },
        filter: rbacDeleteTag.value,
        category: navCommandCategories.remove
      }
    ].filter(({ filter }) => (filter === undefined ? true : filter))

    return filterNavOptionDividers(options)
  })

  const showNavBarOptions = (e: MouseEvent) => {
    if (!rbacChangeLayout.value) {
      return
    }
    contextMenu.show({
      menus: navBarOptions.value,
      x: e.clientX,
      y: e.clientY,
      onSelect: key => handleNavBarComand(key as NavBarCommand)
    })
  }

  function filterNavOptionDividers(options: DropdownOption[]) {
    return options.filter(option => {
      if (option.type === 'divider') {
        const [prevCategory, nextCategory] = (
          (option.categorySpliter || '') as string
        )?.split(':')
        const prevMatched =
          options.filter(o => o.category === prevCategory).length > 0
        const nextMatched =
          options.filter(o => o.category === nextCategory).length > 0
        return prevMatched && nextMatched
      }
      return true
    })
  }

  const showNavOptions = (e: MouseEvent, nav: Nav) => {
    selectedNav.value = nav
    const filteredNavOptions = navOptions.value.filter(option => {
      // 排除【非文档类型】的 "新页面打开" 命令
      const exclude = option.key === NavCommand.OpenInNewTab && !nav.toFile
      return !exclude
    })
    // 还要过滤掉 d1 divider

    contextMenu.show({
      menus: filterNavOptionDividers(filteredNavOptions),
      x: e.clientX,
      y: e.clientY,
      onSelect: key => handleNavCommand(nav, key as NavCommand)
    })
  }

  const toViewContent = (nav: Nav) => {
    router.push({ name: 'Nav', query: { nid: nav.id } })
  }

  const showIcon = computed(
    () => preference.current.layout.navContentLayout !== 'simple'
  )

  const findHighlightNavId = (
    navs: Nav[],
    expandedNavIds: number[],
    nid: number
  ) => {
    const expandedIdMap = expandedNavIds.reduce((prev, key) => {
      prev[key] = true
      return prev
    }, {} as Record<number, boolean>)
    const target = navs.find(nav => nav.id === nid)
    if (!target) {
      return
    }
    const firstCollapseNavId = target.idPath.find(id => !expandedIdMap[id])
    return firstCollapseNavId !== undefined ? firstCollapseNavId : nid
  }

  const selectedTreeNavIds = computed(() => {
    const nid = findHighlightNavId(
      navs.value as Nav[],
      expandedNavIds.value,
      activeNavId.value
    )
    return nid !== undefined ? [nid] : []
  })
  const isDragging = ref(false)
  const dragStart = () => {
    isDragging.value = true
  }
  const dragEnd = () => {
    isDragging.value = false
  }
  const generateNavsWithNewOrder = (
    navs: Nav[],
    targetId: number,
    options: { newParentId?: number; nextNavId: number }
  ) => {
    const { newParentId, nextNavId } = options
    const nextNav = navs.find(nav => nav.id === nextNavId)

    return navs.map(nav => {
      if (nav.id === targetId) {
        const parentId = newParentId !== undefined ? newParentId : nav.parentId
        // 一点点 hack 手段，用整数就不得不改其他导航的位置
        // 没有 nextNav，则认为放在末位
        const position = nextNav
          ? nextNav.position - 0.5
          : Number.MAX_SAFE_INTEGER
        const kvs = Object.keys(nav.params).map(key => ({
          key,
          value: nav.params[key]
        }))
        const parent = navs.find(nav => nav.id === parentId)
        const parentIdPath = parent?.idPath || []

        return new Nav({
          id: nav.id,
          name: nav.name,
          icon: nav.icon,
          kind: nav.type,
          kvs,
          parentId,
          idPath: [...parentIdPath, nav.id].join(','),
          position
        })
      }
      return nav
    })
  }

  const rootId = 0 // 一级节点的 parentId

  const treeNavs = computed(() => {
    const navList = (tempNavs.value || navs.value) as Nav[]
    const visibleIdToNavMap = navList.reduce((prev, nav) => {
      prev[nav.id] = nav as Nav
      return prev
    }, {} as Record<number, Nav>)

    const findVisibleParentId = (nav: Nav): number => {
      // idPath 最后一个 id 是 nav.id 自己
      for (let i = nav.idPath.length - 2; i >= 0; i--) {
        const parentId = nav.idPath[i]
        if (visibleIdToNavMap[parentId]) {
          return parentId
        }
      }
      return rootId
    }

    return buildTree<Nav, TreeNavOption>(navList as Nav[], {
      buildChilden: data =>
        data
          .sort((nav1, nav2) => nav1.position - nav2.position)
          .map(nav => ({
            key: nav.id,
            label: nav.name,
            name: nav.name,
            icon: showIcon.value ? nav.icon : '',
            data: nav,
            visibleParentId: findVisibleParentId(nav)
          })),
      // @ts-ignore
      root: { key: rootId },
      emptyChildren: true,
      checkIsChild: (node, nav) => node.key === findVisibleParentId(nav)
    })
  })

  const treeNavNodeProps = ({ option }: { option: TreeNavOption }) => ({
    onClick(e: MouseEvent) {
      let element = e.target as HTMLElement | null
      let fromSwitcher = false
      while (element) {
        if (element.classList?.contains('n-tree-node-switcher')) {
          fromSwitcher = true
          break
        }
        element = element.parentNode as HTMLElement | null
      }
      if (!fromSwitcher) {
        toViewContent(option.data)
      }
    },
    onContextmenu(e: MouseEvent) {
      e.preventDefault()
      e.stopPropagation()
      showNavOptions(e, option.data)
    },
    class: option.children?.length ? '' : 'n-tree-node--leaf'
  })

  /**
   * @description 根据 nid 找到它的父节点，如果它本身就是一级父节点，则返回本身
   * level 指向上找的层级，如为 1 时，找上一级的节点，
   * 特别的：若为 -1，则认为递归到根节点
   */
  const findParentNav = (
    navs: Nav[],
    nid: number,
    level = -1
  ): Nav | undefined => {
    if (!level) {
      return
    }
    const current = navs.find(nav => nav.id === nid)
    // 进入递归的条件是 level 为正数或 -1，且 current.parentId 有值
    if (current?.parentId && (level === -1 || level > 0)) {
      const nextLevel = level === -1 ? -1 : level - 1
      return findParentNav(navs, current.parentId, nextLevel)
    }
    return current
  }

  const handleTreeNavDrop = async ({
    node,
    dragNode,
    dropPosition
  }: TreeDropInfo) => {
    const draggingNavNode = dragNode as TreeNavOption
    const navNode = node as TreeNavOption
    const navId = draggingNavNode.data?.id
    let visibleParentId = rootId
    let nextNavId = -1
    switch (dropPosition) {
      case 'before': {
        visibleParentId = navNode.visibleParentId
        nextNavId = navNode.data?.id
        break
      }
      case 'after': {
        visibleParentId = navNode.visibleParentId
        const [siblings, nodeIndex] = findSiblingsAndIndex(
          navNode,
          treeNavs.value
        )
        if (siblings && nodeIndex !== null) {
          const nodeNextSiblings = (
            nodeIndex + 1 < siblings.length
              ? siblings[nodeIndex + 1]
              : undefined
          ) as TreeNavOption | undefined
          nextNavId = nodeNextSiblings?.data?.id || -1
        }
        break
      }
      case 'inside': {
        visibleParentId = navNode.data.id
        break
      }
    }
    try {
      tempNavs.value = generateNavsWithNewOrder(navs.value as Nav[], navId, {
        newParentId: visibleParentId,
        nextNavId
      })
      await navigatorStore.updateNavParent(navId, visibleParentId, nextNavId)
    } catch (e) {
      tempNavs.value = undefined
      handleResponseError(e)
    }
  }

  const updateNavOrder = async (navId: number, nextNavId: number) => {
    try {
      tempNavs.value = generateNavsWithNewOrder(navs.value as Nav[], navId, {
        nextNavId
      })
      await navigatorStore.updateNavPosition(navId, nextNavId)
    } catch (e) {
      tempNavs.value = undefined
      handleResponseError(e)
    }
  }

  watch(navs, () => {
    tempNavs.value = undefined
  })

  const checkItemInList = (
    key: number,
    arr?: TreeNode<Nav, TreeNavOption>[]
  ) => {
    if (arr === undefined || arr?.length === 0) {
      return false
    }
    for (let index = 0; index < arr.length; index++) {
      if (arr[index].key === key) {
        if (arr[index].children?.length === 0) {
          return false
        } else {
          return true
        }
      } else {
        if (arr[index].children !== undefined) {
          checkItemInList(key, arr[index].children)
        }
      }
    }
  }
  /* 每次移动节点时，都对storageExpandedNavIds（默认展开的节点）进行一次过滤，
   * 判断每个展开节点否还有子元素，若没有就将该节点从展开数组中剔除
   */
  watch([() => treeNavs.value], () => {
    storageExpandedNavIds.value = storageExpandedNavIds.value.filter(item =>
      checkItemInList(item, treeNavs.value)
    )
  })

  return {
    activeNavId,
    navs,
    selectedNav,
    checkIsNavActive,
    addNav,
    setNav,
    closeAddOrUpdateNavModal: dialog.close,
    addOrUpdateNavDone,
    showNavBarOptions,
    showNavOptions,
    navSortable,
    toViewContent,
    showIcon,
    addSubNav,
    updateNav,
    storageExpandedNavIds,
    expandedNavIds,
    treeNavs,
    selectedTreeNavIds,
    treeNavNodeProps,
    findParentNav,
    handleTreeNavDrop,
    updateNavOrder,
    onExpandedNavIdsChange,
    findHighlightNavId,
    contextMenuVisible: contextMenu.visible,

    isDragging,
    dragEnd,
    dragStart
  }
}

export default useNav
